/**
 * PATH: packages/ui/src/components/email/row/RowIdentity.tsx
 * FILE_ID: UI-EMAIL-ROW-IDENTITY-001
 * PURPOSE: The row's identity + scannable text — avatar + category dot,
 *          sender (or recipient on Sent), unread dot, importance, subject and
 *          one-line preview, with search-token highlighting. The top line is
 *          now the SENDER's alone: name (left, owns the width) + timestamp
 *          (right). No secondary chips compete with it.
 * WHY:     This is the highest-signal zone of the row; isolating it keeps the
 *          composer thin and the "who + what" legible regardless of framing.
 *          The company + mailbox + relation attribution chips used to ride this
 *          top line and, on a ~204px split-view column, ate ~110px and crushed
 *          the sender to "Ele…" while the subject below it showed full width
 *          (operator 2026-06-20: "there is a glitch … so much space on the
 *          left"). Those chips now live on the bottom scrolling strip (RowMeta)
 *          — same recurring class as the v3 action-cluster move: secondary
 *          chrome must never share the identity's line.
 * FLOW:    email + category + highlight → avatar · name+time · subject · preview.
 * RELATED:
 *   - row/RowMeta.tsx: the classification + attribution chip run below.
 *   - avatar.ts / dateUtils.ts: avatar colour + relative time helpers.
 */
import type { CSSProperties, ReactNode } from 'react'
import { ChevronsDown, ChevronsUp } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { highlightMatch } from '../../global-search/highlight'
import type { EmailMessage } from '../types'
import { getAvatarColor, getInitials } from '../avatar'
import { formatRelativeDate } from '../dateUtils'
import type { EmailRowCategoryConfig } from './rowTypes'

// Newsletter senders embed symbols/emoji in subjects + names ("Pivot ✉",
// "➡ Read"). The app font stack ('Inter', system-ui, …) has NO color-emoji
// fallback, so text-presentation symbols (U+2709 ✉, U+27A1 ➡, …) render as a
// cramped monochrome "tofu" glyph mid-subject (operator 2026-06-28: "the
// emoji … is not clean"). `font-variant-emoji: emoji` forces emoji
// presentation and the appended emoji families resolve to a clean COLOUR
// glyph. Purely additive: Latin text still resolves to Inter first, so only
// emoji/symbol codepoints change. The dashboard (src/) doesn't import the
// shared base.css, so this MUST live on the component to travel with it.
const EMOJI_TEXT_STYLE: CSSProperties = {
  fontFamily:
    "'Inter', system-ui, -apple-system, 'Apple Color Emoji', " +
    "'Segoe UI Emoji', 'Noto Color Emoji', sans-serif",
  fontVariantEmoji: 'emoji',
}

export interface RowIdentityProps {
  email: EmailMessage
  category: EmailRowCategoryConfig
  highlightQuery?: string
  onFilterSender?: () => void
  peerName?: string
  peerAddress?: string
  peerPrefix?: string
  /** The host's AI status pip (CardAiTag). Rendered as a small badge on the
   *  avatar's TOP-LEFT corner rather than as its own leading column — one
   *  avatar-anchored gutter instead of three stacked circles
   *  (checkbox · AI dot · avatar) with mismatched margins (operator
   *  2026-06-21: "the left space is so badly structured, random margin"). */
  aiTag?: ReactNode
  /** Classification + attribution cluster (RowMeta) + visibility band, rendered
   *  as the row's bottom line inside the flex-1 text block. */
  footer?: ReactNode
}

/**
 * RowIdentity(email, …) → the avatar + text column for one row.
 */
export function RowIdentity({
  email,
  category,
  highlightQuery,
  onFilterSender,
  peerName,
  peerAddress,
  peerPrefix,
  aiTag,
  footer,
}: RowIdentityProps) {
  const Icon = category.icon
  const peerProvided = !!(peerName || peerAddress)
  const senderDisplay =
    email.from_name || (email.from_address ?? '').split('@')[0] || 'Unknown sender'
  const identityName = peerProvided
    ? peerName || (peerAddress ?? '').split('@')[0] || 'Unknown recipient'
    : senderDisplay
  const avatarKey = peerProvided
    ? peerName || peerAddress || ''
    : email.from_name || email.from_address || ''
  // Defensive: from_address is typed string but the BE can return null for a
  // malformed row — coalesce so the bad record renders "?" instead of
  // throwing TypeError (the 2026-05-07 SAM Email Hub crash).
  const avatarInitialsSource = peerProvided
    ? peerName || (peerAddress ?? '').split('@')[0] || '?'
    : email.from_name || (email.from_address ?? '').split('@')[0] || '?'

  const hq = highlightQuery?.trim() || ''
  const subjectText = email.subject || '(no subject)'
  const previewText = email.body_preview?.slice(0, 80) || 'No preview'

  const importanceLevel = (email.importance || 'normal').toLowerCase()
  const importanceNode =
    importanceLevel === 'high' ? (
      <span title="High importance" className="shrink-0 inline-flex">
        <ChevronsUp className="h-3.5 w-3.5 text-red-500" aria-label="High importance" />
      </span>
    ) : importanceLevel === 'low' ? (
      <span title="Low importance" className="shrink-0 inline-flex">
        <ChevronsDown className="h-3 w-3 text-muted-foreground/50" aria-label="Low importance" />
      </span>
    ) : null

  const senderNameClass = cn(
    'text-sm truncate',
    !email.is_read ? 'font-semibold text-foreground' : 'font-medium text-foreground/80',
  )

  return (
    <>
      {/* Avatar = the single left-gutter anchor. Category dot rides the
          bottom-right; the AI status pip (when present) rides the top-left so
          both glanceable signals hang off ONE circle instead of stacking into
          a ragged column of separate leading glyphs. */}
      <div className="relative shrink-0">
        <div
          className={cn(
            'h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-xs',
            getAvatarColor(avatarKey),
          )}
        >
          {getInitials(avatarInitialsSource)}
        </div>
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-background flex items-center justify-center',
            category.dot,
            category.dotIsMuted && 'opacity-60',
          )}
          title={category.label}
        >
          <Icon className="h-2 w-2 text-white" />
        </span>
        {aiTag ? (
          <span className="absolute -top-1.5 -left-1.5 z-[1] origin-top-left scale-[0.72]">
            {aiTag}
          </span>
        ) : null}
      </div>

      <div className="flex-1 min-w-0" style={EMOJI_TEXT_STYLE}>
        {/* Top line = SENDER only. Name owns the full width (left), timestamp
            pinned right. Every attribution/classification chip now lives on the
            bottom scrolling strip, so the name truncates only at the real edge
            (like the subject below it) instead of being crushed by chrome. */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {onFilterSender ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onFilterSender()
                }}
                title={`Show all emails from ${identityName}`}
                className={cn(senderNameClass, 'min-w-0 text-left hover:underline')}
              >
                {hq ? highlightMatch(identityName, hq, 'sndr') : identityName}
              </button>
            ) : (
              <span className={cn(senderNameClass, 'min-w-0')} title={identityName}>
                {peerProvided && peerPrefix ? (
                  <span className="text-muted-foreground font-normal">{peerPrefix}</span>
                ) : null}
                {hq ? highlightMatch(identityName, hq, 'sndr') : identityName}
              </span>
            )}
            {!email.is_read && (
              <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
            )}
          </div>
          <span className="text-[10px] text-muted-foreground shrink-0">
            {formatRelativeDate(email.received_at)}
          </span>
        </div>

        <div
          className={cn(
            'text-xs mb-0.5 flex items-center gap-1 min-w-0',
            !email.is_read ? 'font-medium text-foreground' : 'text-foreground/70',
          )}
        >
          {importanceNode}
          <span className="truncate">
            {hq ? highlightMatch(subjectText, hq, 'subj') : subjectText}
          </span>
        </div>

        <div className="text-[11px] text-muted-foreground truncate leading-relaxed">
          {hq ? highlightMatch(previewText, hq, 'prev') : previewText}
        </div>

        {footer}
      </div>
    </>
  )
}
